import { nextInt, nextU32, seedRng, shuffled } from './rng.js';
export const WIRES = 4;
/** A card swipe counts when it takes this long, in ms: not a flick, not a crawl. */
export const SWIPE_MIN_MS = 350;
export const SWIPE_MAX_MS = 1_200;
export const CODE_DIGITS = 4;
/** The fuel pump fills an empty tank in this long; the band the server marks is this wide. */
export const FUEL_FULL_MS = 4_000;
export const FUEL_BAND = 15;
/** One pump takes at least this long (the phone can't pump faster than the server saw). */
export const PUMP_MIN_MS = 150;
/** The keke fills in this long at the least, plus up to LOAD_SPREAD_MS more (dealt). */
export const LOAD_MIN_MS = 6_000;
export const LOAD_SPREAD_MS = 3_000;
/** Oil samples on the tray; one is bad. */
export const SAMPLES = 5;
/** Passengers in the crowd, and the looks one can wear. */
export const CROWD = 9;
export const LOOKS = 4;
/** The naira notes on the sort board, smallest first. */
export const NOTES = [5, 10, 20, 50, 100, 200, 500, 1000];
/** One tap takes at least this long (sort, wash): the phone can't tap faster than the server saw. */
export const TAP_MIN_MS = 150;
/** The keke's panels on the wash board, and how many have mud. */
export const PANELS = 9;
export const MUD = 4;
export const gameOf = (t) => t.game ?? 'hold';
/** The deal generator, made on first use from one draw of the match generator. */
function dealRng(s) {
    s.dealRng ??= seedRng(nextU32(s.rng));
    return s.dealRng;
}
/** The secret a stop's game needs, dealt when the stop opens. */
export function deal(s, task) {
    switch (gameOf(task)) {
        case 'wires':
            // wires[i] = the right-hand socket left wire i belongs in
            return { wires: shuffled(dealRng(s), [0, 1, 2, 3]) };
        case 'code':
            return { code: String(nextInt(dealRng(s), 10 ** CODE_DIGITS)).padStart(CODE_DIGITS, '0') };
        case 'fuel': {
            const lo = 40 + nextInt(dealRng(s), 31);
            return { fuel: { lo, hi: lo + FUEL_BAND } };
        }
        case 'pump':
            return { pumps: 8 + nextInt(dealRng(s), 7) };
        case 'clamps':
            return { plus: nextInt(dealRng(s), 2) === 0 ? 'left' : 'right' };
        case 'load':
            return { waitMs: LOAD_MIN_MS + nextInt(dealRng(s), LOAD_SPREAD_MS + 1) };
        case 'inspect':
            return { odd: nextInt(dealRng(s), SAMPLES) };
        case 'spot': {
            const r = dealRng(s);
            const at = nextInt(r, CROWD);
            return { spot: { at, look: nextInt(r, LOOKS) } };
        }
        case 'sort':
            return { notes: shuffled(dealRng(s), NOTES) };
        case 'wash': {
            const panels = shuffled(dealRng(s), Array.from({ length: PANELS }, (_, i) => i));
            return { dirt: panels.slice(0, MUD).sort((a, b) => a - b) };
        }
        default:
            return {};
    }
}
/** Did the phone solve it? `elapsed` is server time since the stop opened. */
export function solved(task, session, outcome, elapsed) {
    switch (gameOf(task)) {
        case 'hold':
            return true;
        case 'wires': {
            const w = outcome?.wires;
            return Array.isArray(w) && w.length === WIRES && w.every((x, i) => x === session.wires?.[i]);
        }
        case 'swipe': {
            const ms = outcome?.swipeMs;
            return (Number.isInteger(ms) &&
                ms >= SWIPE_MIN_MS &&
                ms <= SWIPE_MAX_MS &&
                ms <= elapsed);
        }
        case 'code':
            return typeof outcome?.code === 'string' && outcome.code === session.code;
        case 'fuel': {
            const ms = outcome?.fuelMs;
            const band = session.fuel;
            if (!band || !Number.isInteger(ms) || ms > elapsed)
                return false;
            // integer percent the phone's tank showed: no rounding argument at the band's edges
            const pct = Math.floor((ms * 100) / FUEL_FULL_MS);
            return pct >= band.lo && pct <= band.hi;
        }
        case 'pump': {
            const n = outcome?.pumps;
            return n !== undefined && n === session.pumps && n * PUMP_MIN_MS <= elapsed;
        }
        case 'clamps': {
            const c = outcome?.clamps;
            const minus = session.plus === 'left' ? 'right' : 'left';
            return Array.isArray(c) && c.length === 2 && c[0] === session.plus && c[1] === minus;
        }
        case 'load':
            // the download: no answer but staying put until the keke is full on the server clock
            return session.waitMs !== undefined && elapsed >= session.waitMs;
        case 'inspect':
        case 'spot': {
            const want = gameOf(task) === 'inspect' ? session.odd : session.spot?.at;
            return want !== undefined && Number.isInteger(outcome?.pick) && outcome?.pick === want;
        }
        case 'sort': {
            const notes = session.notes;
            const o = outcome?.order;
            if (!notes || !Array.isArray(o) || o.length !== notes.length)
                return false;
            if (o.length * TAP_MIN_MS > elapsed)
                return false;
            // every tile once, each note bigger than the one before
            if (new Set(o).size !== o.length)
                return false;
            return o.every((tile, i) => {
                const v = notes[tile];
                const prev = i === 0 ? -1 : notes[o[i - 1]];
                return Number.isInteger(tile) && v !== undefined && v > prev;
            });
        }
        case 'wash': {
            const dirt = session.dirt;
            const o = outcome?.scrubbed;
            if (!dirt || !Array.isArray(o) || o.length !== dirt.length || o.length * TAP_MIN_MS > elapsed)
                return false;
            const sorted = [...o].sort((a, b) => a - b);
            return sorted.every((x, i) => x === dirt[i]);
        }
    }
}
/** The tiles of a sort board from the smallest note to the biggest. */
export const sortOrder = (notes) => notes
    .map((v, i) => ({ v, i }))
    .sort((a, b) => a.v - b.v)
    .map((x) => x.i);
/**
 * What a player who plays the stop's game properly sends, and the earliest the server would take
 * it (the floor, and the time the game itself takes). For bots and tests only.
 */
export function answer(task, session) {
    const swipeMs = 800;
    const fuelMs = session.fuel ? Math.ceil((((session.fuel.lo + session.fuel.hi) / 2) * FUEL_FULL_MS) / 100) : 0;
    const pumps = session.pumps ?? 0;
    const outcome = {
        ...(session.wires ? { wires: [...session.wires] } : {}),
        ...(session.code !== undefined ? { code: session.code } : {}),
        ...(gameOf(task) === 'swipe' ? { swipeMs } : {}),
        ...(session.fuel ? { fuelMs } : {}),
        ...(session.pumps !== undefined ? { pumps } : {}),
        ...(session.plus ? { clamps: [session.plus, session.plus === 'left' ? 'right' : 'left'] } : {}),
        ...(session.odd !== undefined ? { pick: session.odd } : {}),
        ...(session.spot ? { pick: session.spot.at } : {}),
        ...(session.notes ? { order: sortOrder(session.notes) } : {}),
        ...(session.dirt ? { scrubbed: [...session.dirt] } : {}),
    };
    const taps = (session.notes?.length ?? 0) + (session.dirt?.length ?? 0);
    const game = gameOf(task) === 'swipe' ? swipeMs : Math.max(fuelMs, pumps * PUMP_MIN_MS, taps * TAP_MIN_MS, session.waitMs ?? 0);
    return { outcome, after: Math.max(task.holdMs, game) };
}
//# sourceMappingURL=minigames.js.map